import { z } from 'zod';

const ApiErrorBodySchema = z
  .object({
    message: z.string().optional(),
    code: z.string().optional(),
    lockedBy: z
      .object({
        userId: z.string().optional(),
        userInitials: z.string().optional(),
      })
      .optional(),
  })
  .passthrough();

export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly details: ApiErrorBody;
  readonly code?: string;
  readonly lockedBy?: ApiErrorBody['lockedBy'];
  readonly isLockedByAnotherUser: boolean;

  constructor(
    source: Response | { status: number; statusText: string },
    body: ApiErrorBody
  ) {
    super(
      body.message ??
        `HTTP error! status: ${source.status} - ${source.statusText}`
    );
    this.name = 'ApiError';
    this.status = source.status;
    this.statusText = source.statusText;
    this.details = body;
    this.code = body.code;
    this.lockedBy = body.lockedBy;
    this.isLockedByAnotherUser = Boolean(body.lockedBy);
  }
}

export async function buildApiError(response: Response): Promise<ApiError> {
  const errorText = await response.text();
  let body: ApiErrorBody = {};
  try {
    body = ApiErrorBodySchema.parse(JSON.parse(errorText));
  } catch {
    // Malformed/empty error body — fall back to default ApiError fields
  }
  return new ApiError(response, body);
}
